/**
 * Values the dashboard shows and the planner enforces. This module imports nothing, so the web
 * app can read from it without pulling in Boulevard, database or job code.
 */

/**
 * Schedule locks (rule fields `startLockHoursBefore` / `endLockHoursBefore`). From that many hours
 * before a shift's start (or end), the BTB block at that edge is not created, resized or removed,
 * so a time staff checked stays put. Counted back from each shift's own edge, so a 9am shift locks
 * earlier than a 3pm one. null = that edge is never locked.
 */
export const DEFAULT_START_LOCK_HOURS_BEFORE = 12;
/** End-of-shift blocks are not locked unless an owner turns it on. */
export const DEFAULT_END_LOCK_HOURS_BEFORE: number | null = null;
/** What the Rules page fills in when an owner turns the end lock on. */
export const END_LOCK_ON_HOURS_BEFORE = 8;
/** Longest lock the rule accepts, in hours before the edge. */
export const MAX_LOCK_HOURS_BEFORE = 48;

/** "1 hour" / "12 hours". */
export const hoursText = (h: number) => `${h} hour${h === 1 ? "" : "s"}`;

/**
 * Suffix on a planned action a schedule lock held back. Stored rows carry it, and rows from before
 * shift-relative locks carry the old clock form (HOLD_REASON_SUFFIX.lockedStart, "… after 8am the
 * same day"); the web recogniser reads both.
 */
export function lockedSuffix(edge: "start" | "end", hoursBefore: number): string {
  return ` (skipped: ${edge} time locked ${hoursText(hoursBefore)} before the shift ${edge === "start" ? "starts" : "ends"})`;
}

/**
 * How many days ahead, starting today in the location's zone, BTB blocks are planned. A rule
 * field (`RuleSet.lookaheadDays`) within these bounds. Days after a location's window but before
 * LOOKAHEAD_MAX_DAYS are planned remove-only, so shortening the window takes our blocks off them.
 */
export const LOOKAHEAD_MIN_DAYS = 7;
export const LOOKAHEAD_MAX_DAYS = 28;
export const DEFAULT_LOOKAHEAD_DAYS = 14;

/**
 * The only values the Rules page offers and `PUT /api/rules` accepts (`checkRuleSteps`). Stored
 * rule sets are never re-checked against these, so an older off-step set keeps planning.
 */
export const RULE_STEPS = {
  blockMinutes: [30, 45, 60, 90, 120, 150, 180],
  tierStartMinutes: [30, 60, 90, 120, 150, 180, 210, 240],
  removalUtilizationPct: [30, 40, 50, 60, 70, 80, 90],
  removalMinGapMinutes: [30, 45, 60, 90, 120],
  minShiftMinutes: [120, 180, 240, 300, 360],
  lookaheadDays: [7, 10, 14, 21, 28],
  // Hours before the shift starts / ends.
  startLockHoursBefore: [2, 4, 6, 8, 10, 12, 14, 16, 18, 24],
  endLockHoursBefore: [2, 4, 6, 8, 10, 12, 14, 16, 18, 24],
  // Protected hours start and end on a multiple of this many minutes.
  protectedHoursStepMinutes: 30,
} as const;

/**
 * Reason text that mutate, evaluate and exclusions write onto action rows, and that the web
 * recogniser (src/web/lib/activity-reason.ts) and skipCategory read back. One literal each so
 * writer and reader cannot drift. Stored rows carry these strings: do not reword.
 */
export const GATE_REASON_PREFIX = {
  disabled: "gate: location not enabled — ",
  paused: "gate: location paused — ",
  globallyPaused: "gate: globally paused — ",
  unmanaged: "gate: block not managed by this system — ",
  // adopt: the manager's block already has a managed_blocks row (another pass adopted it first, or it is ours).
  tracked: "gate: block already tracked by this system — ",
} as const;

export const HOLD_REASON_SUFFIX = {
  elapsedStart: " (skipped: block start has already passed)",
  elapsedEnd: " (skipped: block has already ended)",
  // The fixed 7pm start lock's suffix, on rows written before schedule locks were shift-relative.
  lockedStart: " (skipped: start time locked after 7pm the night before)",
  bookingHold: " (skipped: held for a booking)",
  // adopt: a block of ours on the same edge had to come off first and did not (held or failed).
  adoptWaiting: " (skipped: our block on this edge has not come off yet)",
  // keepSomeoneIn: a removal earlier in the same coverage pass failed, so new blocks wait for the follow-up.
  coverageWaiting: " (skipped: a block that had to come off first did not)",
} as const;

/** Minutes the planner leaves a released edge alone, waiting for the caller's booking to land. */
export const BOOKING_HOLD_MINUTES = 15;

/** Reason on the `remove` row the partner API writes. Stored rows carry it: do not reword. */
export function releasedForBookingReason(clientName: string, reference: string | null): string {
  return `released for a booking via ${clientName}${reference ? ` (ref ${reference})` : ""}`;
}

/** Start of `windDownReason`; blocked-time accounting matches stored reasons on it. */
export const WIND_DOWN_REASON_PREFIX = "location winding down";

export function windDownReason(who: string): string {
  return `${WIND_DOWN_REASON_PREFIX} (set by ${who}): removing this system's BTB blocks, adding none`;
}

/** Start of `pastWindowReason`. */
export const PAST_WINDOW_REASON_PREFIX = "past this location's planning window";

export function pastWindowReason(days: number): string {
  return `${PAST_WINDOW_REASON_PREFIX} (${days} days): removing this system's BTB blocks, adding none`;
}

/** A block of ours on a day the staff member has no shift, or outside the shift they have. */
export const OFF_SHIFT_REASON = "no shift covers the BTB block any more: removing it";

/** A block of ours staff moved onto another staff member's calendar (Boulevard keeps its id). */
export const MOVED_STAFF_REASON = "staff moved the BTB block to another staff member's calendar: removing it";

/**
 * The obstacle the planner names when the end block would run into the start block of the same
 * shift, whether that block is placed in this pass or already there (Parker 2026-10-30).
 */
export const OUR_START_OBSTACLE = "the BTB block at the shift start";

/** An end block already on the calendar that runs into the start block comes off; housekeeping on Results. */
export const EDGE_OVERLAP_REMOVE_REASON = "runs into the BTB block at the shift start: removing it";

/** Start of both exclusion reasons below. */
export const EXCLUDED_REASON_PREFIX = "staff excluded";

export function excludedByReason(scope: "location" | "global", who: string, reason: string | null): string {
  return `${EXCLUDED_REASON_PREFIX} ${scope === "location" ? "at this location" : "at every location"} by ${who}${reason ? `: ${reason}` : ""}`;
}

export function excludedRoleReason(role: string): string {
  return `${EXCLUDED_REASON_PREFIX} automatically: Boulevard role "${role}"`;
}

/** Start of `dayOffReason`: a weekday the owner switched BTB off (protected hours). */
export const DAY_OFF_REASON_PREFIX = "BTB is off on";

export function dayOffReason(weekdayName: string): string {
  return `${DAY_OFF_REASON_PREFIX} ${weekdayName}s here: removing the BTB block`;
}

/** Start of `protectedHoursRemoveReason`; the create and resize skips carry it mid-sentence. */
export const PROTECTED_HOURS_REASON_PREFIX = "inside protected hours";

/** `inside protected hours 11am–4pm: removing the 10:30am–12pm BTB block` */
export function protectedHoursRemoveReason(from: string, to: string, blockFrom: string, blockTo: string): string {
  return `${PROTECTED_HOURS_REASON_PREFIX} ${from}–${to}: removing the ${blockFrom}–${blockTo} BTB block`;
}

/** `480m open at shift start, tier 3 → 90m block, but 9am–10:30am falls inside protected hours 10am–4pm` */
export function protectedHoursCreateReason(gap: number, edge: "start" | "end", tierNumber: number, len: number, blockFrom: string, blockTo: string, from: string, to: string): string {
  return `${gap}m open at shift ${edge}, tier ${tierNumber} → ${len}m block, but ${blockFrom}–${blockTo} falls ${PROTECTED_HOURS_REASON_PREFIX} ${from}–${to}`;
}

/** `wanted to resize to 60m but 9am–10am falls inside protected hours 9:45am–4pm` */
export function protectedHoursResizeReason(len: number, blockFrom: string, blockTo: string, from: string, to: string): string {
  return `wanted to resize to ${len}m but ${blockFrom}–${blockTo} falls ${PROTECTED_HOURS_REASON_PREFIX} ${from}–${to}`;
}

/** Start of the reason on a `lock` action: a block whose schedule lock passed is swapped for a DNB block. */
export const LOCKED_DNB_REASON_PREFIX = "locked in as DNB";

export function lockedDnbReason(edge: "start" | "end", hoursBefore: number): string {
  return `${LOCKED_DNB_REASON_PREFIX}: ${edge} time locked ${hoursText(hoursBefore)} before the shift ${edge === "start" ? "starts" : "ends"}`;
}

/** Reason on the release recorded when adoptManualBtb is switched off: the managers' BTB blocks stay on the calendar, no longer tracked. */
export const MANUAL_OFF_REASON = "managers' BTB blocks are no longer managed here; left as it is";

/** Reason on an `adopt` action: a manager's hand-placed BTB block at a shift edge is now managed (spec 2026-10-27 §4.2). */
export function adoptReason(title: string, minutes: number): string {
  return `took over the manager's BTB block "${title}" (${minutes}m); left as set until a booking changes the gap`;
}

/** Start of the reason on the removal of our block when a manager's BTB block sits on the same edge. */
export const MANAGER_WINS_REASON_PREFIX = "the manager placed a BTB block ";

export function managerWinsReason(title: string): string {
  return `${MANAGER_WINS_REASON_PREFIX}"${title}" on this edge; theirs is kept`;
}

/** Start of every "keep someone in the salon" reason (spec 2026-10-28). */
export const COVERAGE_REASON_PREFIX = "keep someone in:";

export type CoverageWhy = { kind: "alone" } | { kind: "kept" | "fewer" | "last"; names: string[] };

function joinNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** `keep someone in: left open 9am–10am; Ana already got this one`: a block dropped so someone stays in. */
export function coverageCreateReason(from: string, to: string, why: CoverageWhy): string {
  const head = `${COVERAGE_REASON_PREFIX} left open ${from}–${to}; `;
  switch (why.kind) {
    case "alone": return `${head}nobody else is in then`;
    case "kept": return `${head}${joinNames(why.names)} already got this one`;
    case "fewer": return `${head}${joinNames(why.names)} had fewer BTB blocks this week`;
    case "last": return `${head}even count and ${joinNames(why.names)} didn't get the last one`;
  }
}

export function coverageResizeReason(minutes: number, from: string, to: string): string {
  return `${COVERAGE_REASON_PREFIX} wanted to grow to ${minutes}m but nobody else is in ${from}–${to}`;
}

export function coverageRemoveReason(from: string, to: string): string {
  return `${COVERAGE_REASON_PREFIX} block removed so someone is in ${from}–${to}`;
}

/** An Auto - BTB block still sitting next to its DNB copy (the swap's delete failed, or staff re-created it). */
export const LOCK_LEFTOVER_REASON = "leftover BTB block next to its DNB: removing it";
